<template>
  <div class="vt-graph-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$Area = Displacement (x), Slope = Acceleration (a)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>当前速度 v</label>
            <span class="val">{{ currentV.toFixed(2) }} <small>m/s</small></span>
          </div>
          <div class="hud-item orange">
            <label>总位移 x (包围面积)</label>
            <span class="val">{{ totalS.toFixed(1) }} <small>m</small></span>
          </div>
          <div class="hud-item green">
             <label>加速度 a (斜率)</label>
             <span class="val">{{ acc.toFixed(2) }} <small>m/s²</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 450 240" preserveAspectRatio="xMidYMid meet">
          <!-- 面积染色 (Integral Area) -->
          <path :d="areaPath" fill="rgba(6, 182, 212, 0.2)" stroke="none" />

          <!-- 坐标轴 -->
          <g stroke="#334155" stroke-width="1.5">
             <line x1="50" y1="200" x2="400" y2="200" /> <!-- t axis -->
             <line x1="50" y1="40" x2="50" y2="200" />  <!-- v axis -->
          </g>
          <text x="400" y="215" fill="#64748b" font-size="10">时间 t</text>
          <text x="30" y="45" fill="#64748b" font-size="10">速度 v</text>

          <!-- v-t 轨迹线 -->
          <path :d="vtPath" fill="none" stroke="#06b6d4" stroke-width="3" />
          <circle :cx="timeToX(elapsedTime)" :cy="vToY(currentV)" r="5" fill="#fbbf24" />
          
          <!-- 速度向量箭头演示 (物理具象化) -->
          <line x1="100" :y1="vToY(currentV)" 100 :y2="vToY(currentV) - currentV * 2" stroke="#fbbf24" stroke-width="4" marker-end="url(#arr-v)" />
        </svg>

        <div class="physics-caption">
           规律：在 $v-t$ 图像中，曲线与时间轴包围的**面积**代表了物体的**位移(总距离)**。
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">调节初速度 v₀</label>
                 <el-slider v-model="v0" :min="-10" :max="10" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">调节加速度 a (改变斜率)</label>
                 <el-slider v-model="acc" :min="-2" :max="2" :step="0.1" size="small"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button type="primary" size="small" @click="toggleAnim">{{ isRunning ? '停止' : '开始运动' }}</el-button>
                 <el-button size="small" @click="reset">重置</el-button>
              </div>
           </div>
        </div>
        
        <!-- 高对比度文字卡片加固 -->
        <div class="discovery-card-discovery-tir" v-latex>
           <div class="icon-wrap"><i class="el-icon-odometer"></i></div>
           <div class="text-content">
             <p><strong>实战物理：</strong>$v-t$ 图像是描述运动最高效的工具。<strong>观察关键点：</strong>
                1. 调节加速度滑块：你会发现直线变陡（速度大幅增长/下降）；
                2. 面积动态：随着运行时间 $t$ 变长，你看到下方**阴影面积不断扩大**，这个面积就是物体跑出的真实里程。
                这解释了为何匀加速位移公式是 $x = v₀t + \frac{1}{2}at^2$ —— 它分拆开正是矩形面积加三角形面积之和。
             </p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'VT_GraphViz',
  data() {
    return {
      v0: 2,
      acc: 0.5,
      elapsedTime: 0,
      pathPoints: [],
      isRunning: false,
      nextId: 0
    }
  },
  computed: {
    currentV() { return this.v0 + this.acc * this.elapsedTime; },
    totalS() { // Simpson's logic: Displacement = v0*t + 0.5*a*t^2
       return this.v0 * this.elapsedTime + 0.5 * this.acc * Math.pow(this.elapsedTime, 2);
    },
    vtPath() {
       if (this.pathPoints.length < 2) return "";
       let p = `M ${this.timeToX(this.pathPoints[0].t)},${this.vToY(this.pathPoints[0].v)}`;
       this.pathPoints.forEach(pt => {
          p += ` L ${this.timeToX(pt.t)},${this.vToY(pt.v)}`;
       });
       return p;
    },
    areaPath() {
       if (this.pathPoints.length < 2) return "";
       let p = `M ${this.timeToX(0)},200 L ${this.timeToX(0)},${this.vToY(this.pathPoints[0].v)}`;
       this.pathPoints.forEach(pt => {
          p += ` L ${this.timeToX(pt.t)},${this.vToY(pt.v)}`;
       });
       return p + ` L ${this.timeToX(this.elapsedTime)},200 Z`;
    }
  },
  methods: {
    toggleAnim() { this.isRunning = !this.isRunning; if(this.isRunning) this.animate(); },
    animate() {
       if (!this.isRunning) return;
       this.elapsedTime += 0.05;
       this.pathPoints.push({ t: this.elapsedTime, v: this.currentV });
       if (this.elapsedTime > 20) this.reset();
       requestAnimationFrame(this.animate);
    },
    reset() {
       this.elapsedTime = 0; this.pathPoints = []; this.isRunning = false;
    },
    timeToX(t) { return 50 + t * 15; },
    vToY(v) { return 150 - v * 8; }
  },
  beforeDestroy() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    if (this.timer) clearInterval(this.timer);
  },
}
</script>

<style scoped lang="scss">
@import "./PhysicsCommon.scss";
.vt-graph-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
</style>
